'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { InquiryFlowSnapshot } from '@miso/design-system';
import { createProgressFlowFetcher } from '@/shared/api/inquiry-flow/fetchProgressFlow';
import { ensureIntakeSessionStarted } from '../model/ensureIntakeSessionStarted';
import { MOVING_DATE_QUESTION_ID } from '../model/staticFlows';
import { StepFlow } from './StepFlow';

const QUESTION_ID = MOVING_DATE_QUESTION_ID;
const FLOW_ID = 'progress-moving-date';

interface Props {
  visitCounselId: string;
  counsel: VisitCounselView;
  /**
   * 신청 시 고객이 입력한 이사일 (`order.moving.moveDate`, `YYYY-MM-DD`).
   *
   * `counsel.movingDate` 가 아직 없을 때(첫 진입) 날짜 입력창의 기본값으로 채운다.
   * 이미 counsel 에 저장된 값(`counsel.movingDate`)이 있으면 그 값이 우선된다.
   */
  orderMoveDate?: string | null;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  onAfterDone: (counsel: VisitCounselView) => void;
  onCancel: () => void;
  /** 이전 sub-stage 로부터 뒤로가기 진입 — 마지막 visible 질문에 위치. */
  startAtLast?: boolean;
  progressOffset?: number;
  progressTotal?: number;
  onStateChange?: (snap: InquiryFlowSnapshot) => void;
}

/**
 * 이사일자 step (Figma `219:10868`).
 *
 * date input 한 개. 저장 시 `recordMovingDate` 호출.
 */
export function MovingDateStepAdapter({
  visitCounselId,
  counsel,
  orderMoveDate,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
  startAtLast,
  progressOffset,
  progressTotal,
  onStateChange,
}: Props) {
  // Firestore `playground_flows/progress-moving-date` 의 FlowDocument 를 비동기 로드.
  // 등록 절차는 `application/scripts/register-flows.ts` 참조.
  const fetcher = useMemo(() => createProgressFlowFetcher(FLOW_ID), []);

  const initialValues = useMemo(() => {
    // counsel 에 이미 저장된 이사일이 있으면 그 값 우선.
    // 없으면 신청 시 고객이 입력한 이사일(`orderMoveDate`)을 기본값으로 채운다.
    const date = counsel.movingDate ?? orderMoveDate;
    if (!date) return undefined;
    return { [QUESTION_ID]: date };
  }, [counsel.movingDate, orderMoveDate]);

  // 마운트 시점에 intake-session REQUESTED → STARTED 보장. 이미 STARTED/COMPLETED 면 no-op.
  useEffect(() => {
    void ensureIntakeSessionStarted(visitCounselId, counsel);
    // 마운트 1회만 실행 — counsel.sessions.intake.status 변화는 helper 가 알아서 가드.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = useCallback(
    async (values: Record<string, unknown>) => {
      const movingDate = values[QUESTION_ID];
      if (typeof movingDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(movingDate)) {
        showToast('이사일자를 선택해주세요.', 'warning');
        return;
      }
      try {
        await ensureIntakeSessionStarted(visitCounselId, counsel);
        await myVisitCounselApi.recordMovingDate(visitCounselId, { movingDate });
        showToast('이사일자가 저장되었습니다.', 'success');
        const result = await refetch();
        const updated = result?.counsel ?? null;
        if (updated) onAfterDone(updated);
        else onCancel();
      } catch (err) {
        showToast(err instanceof Error ? err.message : '이사일자 저장에 실패했습니다.', 'error');
      }
    },
    [visitCounselId, counsel, showToast, refetch, onAfterDone, onCancel]
  );

  // moving-date 는 단일 질문이라 startAtLast 가 활성이어도 결과는 같지만,
  // 어댑터 prop 일관성을 위해 그대로 받아 InquiryFlow 에 전달한다.
  const startAtQuestionId = startAtLast ? QUESTION_ID : undefined;

  return (
    <StepFlow
      fetcher={fetcher}
      initialValues={initialValues}
      onComplete={handleComplete}
      onCancel={onCancel}
      onError={(err) => showToast(err.message ?? '플로우 오류가 발생했습니다.', 'error')}
      startAtQuestionId={startAtQuestionId}
      progressOffset={progressOffset}
      progressTotal={progressTotal}
      onStateChange={onStateChange}
    />
  );
}
